<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import { hasCannedContentOrFiles } from 'dashboard/helper/cannedResponseHelper';

export default {
  name: 'AddCanned',
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
  },
  props: {
    responseContent: {
      type: String,
      default: '',
    },
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      shortCode: '',
      content: this.responseContent || '',
      files: [],
      addCanned: {
        showLoading: false,
        message: '',
      },
      show: true,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
  },
  computed: {
    hasContentOrFiles() {
      return hasCannedContentOrFiles(this.content, this.files);
    },
  },
  methods: {
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.files = [];
      this.v$.shortCode.$reset();
    },
    onFileChange(event) {
      const selectedFiles = Array.from(event.target.files);
      this.files = [...this.files, ...selectedFiles];
    },
    removeFile(index) {
      this.files.splice(index, 1);
    },
    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / 1048576).toFixed(1) + ' MB';
    },
    addCannedResponse() {
      // Show loading on button
      this.addCanned.showLoading = true;
      // Make API Calls
      this.$store
        .dispatch('createCannedResponse', {
          short_code: this.shortCode,
          content: this.content,
          files: this.files,
        })
        .then(() => {
          // Reset Form, Show success message
          this.addCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.ADD.API.SUCCESS_MESSAGE'));
          this.resetForm();
          this.onClose();
        })
        .catch(error => {
          this.addCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.ADD.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('CANNED_MGMT.ADD.TITLE')"
        :header-content="$t('CANNED_MGMT.ADD.DESC')"
      />
      <form class="flex flex-col w-full" @submit.prevent="addCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.SHORT_CODE.PLACEHOLDER')"
              @blur="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label>
            {{ $t('CANNED_MGMT.ADD.FORM.CONTENT.LABEL') }}
            <span class="text-xs text-n-slate-10">
              {{ $t('CANNED_MGMT.ADD.FORM.CONTENT.OPTIONAL_HINT') }}
            </span>
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
            />
          </div>
        </div>
        <div class="w-full mt-2">
          <label>
            {{ $t('CANNED_MGMT.ADD.FORM.FILES.LABEL') }}
          </label>
          <div class="flex items-center gap-2">
            <label
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-xl cursor-pointer bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 dark:hover:bg-n-solid-3 border border-n-weak"
            >
              <span class="i-ri-attachment-line size-3.5" />
              {{ $t('CANNED_MGMT.ADD.FORM.FILES.BUTTON') }}
              <input
                type="file"
                multiple
                class="hidden"
                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,audio/*,video/*"
                @change="onFileChange"
              />
            </label>
          </div>
          <div v-if="files.length > 0" class="mt-2 space-y-1">
            <div
              v-for="(file, index) in files"
              :key="index"
              class="flex items-center justify-between px-2 py-1 rounded bg-n-alpha-2"
            >
              <span class="text-sm truncate text-n-slate-11">
                {{ file.name }} ({{ formatFileSize(file.size) }})
              </span>
              <button
                type="button"
                class="text-n-slate-10 hover:text-red-500"
                @click="removeFile(index)"
              >
                <span class="i-ri-close-line size-3" />
              </button>
            </div>
          </div>
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.ADD.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.ADD.FORM.SUBMIT')"
            :disabled="
              !hasContentOrFiles ||
              v$.shortCode.$invalid ||
              addCanned.showLoading
            "
            :is-loading="addCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
:deep(.ProseMirror-menubar) {
  @apply hidden;
}

:deep(.ProseMirror-woot-style) {
  @apply min-h-[12.5rem];

  p {
    @apply text-base;
  }
}
</style>
